import {
  GeneralPermissions,
  SettingsPermissions,
} from "@redotech/account-common/permissions";
import { PermissionedRouteObject } from "@redotech/redo-merchant-app-common/routing/protected-routes";
import { Navigate } from "react-router-dom";

export const generalSettingsRoutes: PermissionedRouteObject[] = [
  {
    handle: {
      breadcrumb: "Billing",
      permission: [
        SettingsPermissions.SET_GENERAL_SETTINGS,
        SettingsPermissions.VIEW_BILLING,
        SettingsPermissions.SET_BILLING_SETTINGS,
      ],
    },
    path: "billing",
    children: [
      {
        index: true,
        lazy: async () => {
          const { BillingPage } =
            await import("../../../../setting/billing/billing");
          return { Component: BillingPage };
        },
      },
      {
        handle: {
          breadcrumb: "Inventory management",
          permission: SettingsPermissions.SET_GENERAL_SETTINGS,
        },
        path: "ims",
        children: [
          {
            lazy: async () => {
              const { ImsBillingDetail } =
                await import("../../../../setting/billing/products/ims/ims-billing-detail");
              return { Component: ImsBillingDetail };
            },
            children: [
              { index: true, element: <Navigate replace to="subscription" /> },
              {
                path: "subscription",
                lazy: async () => {
                  const { ImsBilling } =
                    await import("../../../../setting/billing/products/ims/ims-billing");
                  return { Component: ImsBilling };
                },
              },
              {
                path: "forecasting",
                lazy: async () => {
                  const { ImsForecastingBilling } =
                    await import("../../../../setting/billing/products/ims/ims-forecasting-billing");
                  return { Component: ImsForecastingBilling };
                },
              },
            ],
          },
        ],
      },
      {
        handle: {
          breadcrumb: "Outbound",
          permission: SettingsPermissions.SET_GENERAL_SETTINGS,
        },
        path: "outbound",
        children: [
          {
            lazy: async () => {
              const { OutboundBillingDetail } =
                await import("../../../../setting/billing/products/outbound-billing-detail");
              return { Component: OutboundBillingDetail };
            },
            children: [
              { index: true, element: <Navigate replace to="general" /> },
              {
                path: "general",
                lazy: async () => {
                  const { BalanceTransactionTable } =
                    await import("../../../../setting/billing/outbound-labels-ledger/balance-transaction-table");
                  return { Component: BalanceTransactionTable };
                },
              },
              {
                path: "balance",
                lazy: async () => {
                  const { OutboundLabelBalancePage } =
                    await import("../../../../setting/outbound-label-balance");
                  return { Component: OutboundLabelBalancePage };
                },
              },
              {
                path: "disputes",
                lazy: async () => {
                  const { SurchargeDisputeTable } =
                    await import("../../../../setting/billing/surcharge-disputes/surcharge-dispute-table");
                  return { Component: SurchargeDisputeTable };
                },
              },
            ],
          },
          {
            async lazy() {
              const { InvoiceDetailPageWrapper, breadcrumb } =
                await import("../../../../setting/billing/outbound-labels-ledger/invoice-detail-page-wrapper");
              return {
                Component: InvoiceDetailPageWrapper,
                handle: {
                  breadcrumb,
                  permission: SettingsPermissions.SET_GENERAL_SETTINGS,
                },
              };
            },
            path: "outbound-invoice/:invoiceId",
          },
        ],
      },
      {
        handle: {
          breadcrumb: "Returns",
          permission: SettingsPermissions.SET_GENERAL_SETTINGS,
        },
        path: "returns",
        children: [
          {
            lazy: async () => {
              const { ReturnsBillingDetail } =
                await import("../../../../setting/billing/products/returns/returns-billing-detail");
              return { Component: ReturnsBillingDetail };
            },
            children: [
              {
                index: true,
                lazy: async () => {
                  const { ReturnsBillingIndexRedirect } =
                    await import("../../../../setting/billing/products/returns/returns-billing-detail");
                  return { Component: ReturnsBillingIndexRedirect };
                },
              },
              {
                path: "general",
                lazy: async () => {
                  const { ReturnsBilling } =
                    await import("../../../../setting/billing/products/returns/returns-billing");
                  return { Component: ReturnsBilling };
                },
              },
            ],
          },
        ],
      },
      {
        handle: {
          breadcrumb: "Supplies",
          permission: SettingsPermissions.SET_BILLING_SETTINGS,
        },
        path: "supplies",
        lazy: async () => {
          const { RedoSuppliesBillingPage } =
            await import("../../../../setting/billing/payment-methods/supplies-billing-page");
          return { Component: RedoSuppliesBillingPage };
        },
      },
      {
        path: ":productSlug",
        lazy: async () => {
          const { BillingProductDetail, productBreadcrumb } =
            await import("../../../../setting/billing/billing-product-detail");
          return {
            Component: BillingProductDetail,
            handle: {
              breadcrumb: productBreadcrumb,
              permission: SettingsPermissions.SET_GENERAL_SETTINGS,
            },
          };
        },
      },
    ],
  },
  {
    handle: {
      breadcrumb: "Brand kit",
      permission: SettingsPermissions.SET_GENERAL_SETTINGS,
    },
    path: "brand-kit",
    lazy: async () => {
      const { BrandKitPage } =
        await import("@redotech/redo-merchant-general-app/setting/brand-kit/brand-kit");
      return { Component: BrandKitPage };
    },
  },
  {
    handle: {
      breadcrumb: "Communication",
      permission: SettingsPermissions.SET_GENERAL_SETTINGS,
    },
    path: "communication",
    lazy: async () => {
      const { CommunicationPage } =
        await import("@redotech/redo-merchant-general-app/setting/communication/communication");
      return { Component: CommunicationPage };
    },
  },
  {
    handle: {
      breadcrumb: "Customer Portal",
      permission: [
        SettingsPermissions.SET_GENERAL_SETTINGS,
        SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      ],
    },
    path: "customer-portal",
    children: [
      {
        index: true,
        lazy: async () => {
          const { CustomerPortalPage: CustomerPortal } =
            await import("@redotech/redo-merchant-general-app/setting/customer-portal/customer-portal-page");
          return { Component: CustomerPortal };
        },
      },
      {
        handle: {
          breadcrumb: "Bundles",
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "bundles",
        lazy: async () => {
          const { BundlesPage } =
            await import("../../../../setting/bundles/bundles");
          return { Component: BundlesPage };
        },
      },
      {
        handle: {
          breadcrumb: "Exchange Agent Guidance",
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "guidance",
        lazy: async () => {
          const { ExchangeAgentGuidancePage } =
            await import("../../../../setting/exchange-agent-guidance/exchange-agent-guidance-page");
          return { Component: ExchangeAgentGuidancePage };
        },
      },
      {
        handle: {
          breadcrumb: "Flows",
          breadcrumbNavigate: false,
          permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
        },
        path: "flows",
        children: [
          {
            handle: {
              breadcrumb: "AB test analytics",
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "ab-test-analytics",
            lazy: async () => {
              const { ReturnFlowABTestAnalytics } =
                await import("../../../../setting/return-flow/ab-test-analytics");
              return { element: <ReturnFlowABTestAnalytics /> };
            },
          },
          {
            handle: {
              breadcrumb: "Claims",
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "claims",
            lazy: async () => {
              const { ClaimsFlowEditor } =
                await import("../../../../setting/flows/claims-flow-editor");
              return { element: <ClaimsFlowEditor /> };
            },
          },
          {
            handle: {
              breadcrumb: "Claims-finalize",
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "claims-finalize",
            lazy: async () => {
              const { ClaimsFinalizeFlowEditor } =
                await import("../../../../setting/flows/claims-finalize-flow-editor");
              return { element: <ClaimsFinalizeFlowEditor /> };
            },
          },
          {
            handle: {
              breadcrumb: "Returns",
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "returns",
            lazy: async () => {
              const { ReturnsFlowEditor } =
                await import("../../../../setting/flows/returns-flow-editor");
              return { element: <ReturnsFlowEditor /> };
            },
          },
          {
            handle: {
              breadcrumb: "Returns-finalize",
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "returns-finalize",
            lazy: async () => {
              const { ReturnsFinalizeFlowEditor } =
                await import("../../../../setting/flows/returns-finalize-flow-editor");
              return { element: <ReturnsFinalizeFlowEditor /> };
            },
          },
          {
            handle: {
              breadcrumb: "Warranties",
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "warranties",
            lazy: async () => {
              const { WarrantiesFlowEditor } =
                await import("../../../../setting/flows/warranties-flow-editor");
              return { element: <WarrantiesFlowEditor /> };
            },
          },
          {
            handle: {
              breadcrumb: "Warranties-finalize",
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "warranties-finalize",
            lazy: async () => {
              const { WarrantiesFinalizeFlowEditor } =
                await import("../../../../setting/flows/warranties-finalize-flow-editor");
              return { element: <WarrantiesFinalizeFlowEditor /> };
            },
          },
          {
            handle: {
              breadcrumb: "Warranties-registration",
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "warranties-registration",
            lazy: async () => {
              const { WarrantiesRegistrationFlowEditor } =
                await import("../../../../setting/flows/warranties-registration-flow-editor");
              return { element: <WarrantiesRegistrationFlowEditor /> };
            },
          },
          {
            handle: {
              breadcrumb: "Withdrawals",
              permission: SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
            },
            path: "withdrawals",
            lazy: async () => {
              const { WithdrawalsFlowEditor } =
                await import("../../../../setting/flows/withdrawals-flow-editor");
              return { element: <WithdrawalsFlowEditor /> };
            },
          },
        ],
      },
    ],
  },
  // We moved /return-portal -> /customer-portal, so we'll redirect users just in case.
  {
    path: "return-portal",
    element: <Navigate replace to="../customer-portal" />,
  },
  {
    handle: {
      breadcrumb: "Customer widget",
      permission: [
        SettingsPermissions.SET_GENERAL_SETTINGS,
        SettingsPermissions.SET_AI_SETTINGS,
        SettingsPermissions.SET_CUSTOMER_ACCOUNTS_SETTINGS,
        SettingsPermissions.SET_SUPPORT_SETTINGS,
      ],
    },
    path: "customer-widget",
    lazy: async () => {
      const { CustomerWidgetSettingsPage } =
        await import("../../../../setting/customer-widget/customer-widget-settings");
      return { Component: CustomerWidgetSettingsPage };
    },
  },
  {
    handle: {
      breadcrumb: "Developer",
      permission: GeneralPermissions.MANAGE_TEAM_DEVELOPMENT,
    },
    path: "developer",
    children: [
      {
        path: "",
        lazy: async () => {
          const { DeveloperPage } =
            await import("@redotech/redo-merchant-general-app/setting/developer");
          return { Component: DeveloperPage };
        },
      },
      {
        handle: { breadcrumb: "Webhook Details" },
        path: "webhooks/:webhookId",
        lazy: async () => {
          const { WebhookDetailPage } =
            await import("@redotech/redo-merchant-general-app/setting/developer/webhook-detail-page");
          return { Component: WebhookDetailPage };
        },
      },
    ],
  },
  {
    handle: {
      breadcrumb: "Groups",
      permission: GeneralPermissions.MANAGE_TEAM_USERS,
    },
    path: "user-groups",
    lazy: async () => {
      const { UserGroupsPage } =
        await import("../../../../setting/user-groups/user-groups-page");
      return { Component: UserGroupsPage };
    },
  },
  { path: "invoices", element: <Navigate replace to="../billing/bills" /> },
  {
    handle: {
      breadcrumb: "Notifications",
      permission: SettingsPermissions.SET_GENERAL_SETTINGS,
    },
    path: "notifications",
    lazy: async () => {
      const { NotificationsPage } =
        await import("@redotech/redo-merchant-general-app/setting/notification/notifications");
      return { Component: NotificationsPage };
    },
  },
  {
    handle: {
      breadcrumb: "Portal Builder",
      permission: [
        SettingsPermissions.SET_GENERAL_SETTINGS,
        SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
      ],
    },
    path: "portal-builder",
    lazy: async () => {
      const { CustomerPortalBuilderSettingsPage } =
        await import("@redotech/redo-merchant-general-app/setting/customer-portal-builder/customer-portal-builder-settings");
      return { Component: CustomerPortalBuilderSettingsPage };
    },
  },
  {
    handle: {
      breadcrumb: "Roles",
      permission: GeneralPermissions.MANAGE_TEAM_ROLES,
    },
    path: "roles",
    lazy: async () => {
      const { RolesSettingsPage } =
        await import("@redotech/redo-merchant-general-app/setting/roles/roles-settings");
      return { Component: RolesSettingsPage };
    },
  },
  {
    handle: {
      breadcrumb: "Users",
      permission: GeneralPermissions.MANAGE_TEAM_USERS,
    },
    path: "users",
    children: [
      {
        path: "",
        lazy: async () => {
          const { UsersPage } =
            await import("@redotech/redo-merchant-general-app/setting/user/user");
          return { Component: UsersPage };
        },
      },
      {
        handle: { breadcrumb: "User information" },
        path: ":userId",
        lazy: async () => {
          const { ProfilePage } =
            await import("@redotech/redo-merchant-general-app/setting/user/profile-page");
          return { Component: ProfilePage };
        },
      },
    ],
  },
];
